import { Button, IconButton, Spinner } from '@chakra-ui/react'
import { useRef, useState } from 'react'
import { LuShare2 } from 'react-icons/lu'
import { toaster } from './toaster'
import { Tooltip } from './tooltip'
import { shareVideo } from '../../services/videoShare'
import { useTranslation } from '../../i18n'

/**
 * Hand a video to the platform share sheet.
 *
 * Shaped like `DownloadButton` on purpose — the two sit next to each other and
 * differ only in what they do with the Cloudflare MP4. `source` is the same
 * `{ shareId }` / `{ kind, id }` pair; `url` is what gets shared when the
 * platform will not take a file, so pass the page a recipient should land on,
 * not the raw storage object.
 *
 * Where the Web Share API is missing entirely — most desktop browsers — the
 * button copies that same link instead of disappearing, so it is never a dead
 * end on a page whose whole purpose is passing a video along.
 */
export function ShareButton({
    source,
    url,
    title,
    cachedDirect,
    cfUid,
    label,
    iconOnly = false,
    tooltip,
    ...rest
}) {
    const { t } = useTranslation()
    const [status, setStatus] = useState(null)
    const busy = status !== null
    const buttonLabel = label ?? t('common.share')
    // A rendition is minted once and then cached by Cloudflare, but the fetch
    // is not — guard against a double click starting a second one.
    const inFlight = useRef(false)

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(url)
            toaster.create({ title: t('common.linkCopied'), type: 'success' })
        } catch {
            toaster.create({ title: t('common.shareFailed'), type: 'error' })
        }
    }

    const handleClick = async (e) => {
        e?.stopPropagation?.()
        if (busy || inFlight.current || !url) return
        inFlight.current = true
        setStatus('preparing')
        try {
            const outcome = await shareVideo({ source, url, title, cachedDirect, cfUid, onStatus: setStatus })
            if (outcome === 'unsupported') await copyLink()
            else if (outcome === 'failed') toaster.create({ title: t('common.shareFailed'), type: 'error' })
        } finally {
            inFlight.current = false
            setStatus(null)
        }
    }

    // Minting the MP4 can take a while on a long reel, so say so rather than
    // leaving a bare spinner.
    const activeTooltip = status === 'preparing' ? t('common.preparingShare') : tooltip

    const button = iconOnly ? (
        <IconButton
            aria-label={status === 'preparing' ? t('common.preparingShare') : buttonLabel}
            onClick={handleClick}
            disabled={busy}
            {...rest}
        >
            {busy ? <Spinner size="sm" /> : <LuShare2 />}
        </IconButton>
    ) : (
        <Button variant="outline" onClick={handleClick} disabled={busy} {...rest}>
            {busy ? <Spinner size="sm" /> : <LuShare2 />}
            {status === 'preparing' ? t('common.preparingShare') : buttonLabel}
        </Button>
    )

    if (activeTooltip) {
        return <Tooltip content={activeTooltip} openDelay={300} closeDelay={100}>{button}</Tooltip>
    }
    return button
}
